import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faEdit, faTrash, faClock, faTag } from '@fortawesome/free-solid-svg-icons';
import prayerService from '../../api/prayerService';
import '../../styles/group/PrayerEditModal.css';
import TimePicker from '../ui/time-picker';

const PrayerEditModal = ({ 
  isOpen, 
  onClose, 
  groupId, 
  selectedDay, 
  prayerType, 
  prayerTimes = [], 
  onPrayerTimesUpdate 
}) => {
  const [editingTimes, setEditingTimes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Convert 12-hour format (7:30 AM) to 24-hour format (07:30) for time input
  const convertTo24Hour = (time12h) => {
    if (!time12h) return '';
    
    try {
      const [time, modifier] = time12h.split(' ');
      let [hours, minutes] = time.split(':');
      
      if (hours === '12') {
        hours = '00';
      }
      
      if (modifier === 'PM') {
        hours = parseInt(hours, 10) + 12;
      }
      
      return `${hours.toString().padStart(2, '0')}:${minutes}`;
    } catch (error) {
      console.error('Error converting time to 24-hour format:', error);
      return '';
    }
  };

  // Convert 24-hour format (07:30) to 12-hour format (7:30 AM) for display
  const convertTo12Hour = (time24h) => {
    if (!time24h) return '';
    
    try {
      const [hours, minutes] = time24h.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      
      return `${hour12}:${minutes} ${ampm}`;
    } catch (error) {
      console.error('Error converting time to 12-hour format:', error);
      return time24h;
    }
  };

  // Initialize editing times when modal opens
  useEffect(() => {
    if (isOpen) {
      setEditingTimes(prayerTimes.map(time => ({ 
        ...time, 
        isEditing: false,
        // Store both formats for easier handling
        time24h: convertTo24Hour(time.time),
        time12h: time.time
      })));
      setError('');
    }
  }, [isOpen, prayerTimes]);

  // Add new prayer time
  const handleAddNew = () => {
    const newTime = {
      id: `temp_${Date.now()}`,
      time: '',
      time24h: '',
      time12h: '',
      description: '',
      isEditing: true,
      isNew: true
    };
    setEditingTimes([...editingTimes, newTime]);
  };

  // Start editing existing time
  const handleEdit = (index) => {
    const updated = [...editingTimes];
    updated[index] = { ...updated[index], isEditing: true };
    setEditingTimes(updated);
  };

  // Cancel editing
  const handleCancel = (index) => {
    const updated = [...editingTimes];
    if (updated[index].isNew) {
      // Remove new item
      updated.splice(index, 1);
    } else {
      // Revert to original
      const original = prayerTimes.find(t => t.id === updated[index].id);
      updated[index] = { 
        ...original,
        isEditing: false,
        time24h: convertTo24Hour(original.time),
        time12h: original.time
      };
    }
    setEditingTimes(updated);
  };

  // Update time (handle time picker input)
  const handleTimeChange = (index, time24h) => {
    const updated = [...editingTimes];
    const time12h = convertTo12Hour(time24h);
    
    updated[index] = { 
      ...updated[index], 
      time24h,
      time12h,
      time: time12h // This is what gets saved to the database
    };
    setEditingTimes(updated);
  };

  // Update description
  const handleDescriptionChange = (index, description) => {
    const updated = [...editingTimes];
    updated[index] = { ...updated[index], description };
    setEditingTimes(updated);
  };

  // Save prayer time (create or update)
  const handleSave = async (index) => {
    const timeData = editingTimes[index];
    
    // Only require time, description is optional
    if (!timeData.time12h.trim()) {
      setError('Time is required');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      if (timeData.isNew) {
        // Create new prayer time
        const response = await prayerService.createPrayerTime(groupId, {
          dayOfWeek: selectedDay,
          prayerType: prayerType,
          time: timeData.time12h.trim(), // Save in 12-hour format
          description: timeData.description.trim() || 'Service' // Default description if empty
        });

        // Update with real ID from server
        const updated = [...editingTimes];
        updated[index] = {
          ...response.data,
          isEditing: false,
          isNew: false,
          time24h: convertTo24Hour(response.data.time),
          time12h: response.data.time
        };
        setEditingTimes(updated);
      } else {
        // Update existing prayer time
        const response = await prayerService.updatePrayerTime(groupId, timeData.id, {
          time: timeData.time12h.trim(), // Save in 12-hour format
          description: timeData.description.trim() || 'Service' // Default description if empty
        });

        // Update in state
        const updated = [...editingTimes];
        updated[index] = { 
          ...response.data, 
          isEditing: false,
          time24h: convertTo24Hour(response.data.time),
          time12h: response.data.time
        };
        setEditingTimes(updated);
      }

      // Notify parent component to refresh data and wait for it to complete
      await onPrayerTimesUpdate();
      
      // Close modal immediately after data refresh completes
      onClose();
    } catch (error) {
      console.error('Error saving prayer time:', error);
      setError(error.response?.data?.message || 'Failed to save prayer time');
    } finally {
      setIsLoading(false);
    }
  };

  // Delete prayer time
  const handleDelete = async (index) => {
    const timeData = editingTimes[index];
    
    if (timeData.isNew) {
      // Just remove from local state
      const updated = [...editingTimes];
      updated.splice(index, 1);
      setEditingTimes(updated);
      return;
    }

    if (!window.confirm('Are you sure you want to delete this prayer time?')) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await prayerService.deletePrayerTime(groupId, timeData.id);
      
      // Remove from state
      const updated = [...editingTimes];
      updated.splice(index, 1);
      setEditingTimes(updated);

      // Notify parent component to refresh data
      onPrayerTimesUpdate();
      
      // Close modal after successful delete to show updated prayer tab
      setTimeout(() => {
        onClose();
      }, 500); // Small delay to show the success state briefly
    } catch (error) {
      console.error('Error deleting prayer time:', error);
      setError(error.response?.data?.message || 'Failed to delete prayer time');
    } finally {
      setIsLoading(false);
    }
  };

  // Save all unsaved changes and close
  const handleDoneWithSave = async () => {
    // Check if there are any items being edited
    const itemsBeingEdited = editingTimes.filter(item => item.isEditing);
    
    if (itemsBeingEdited.length === 0) {
      // Nothing to save, just close
      onClose();
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Save all items that are being edited
      for (let i = 0; i < editingTimes.length; i++) {
        const timeData = editingTimes[i];
        
        if (timeData.isEditing) {
          // Validate required fields
          if (!timeData.time12h.trim()) {
            setError(`Time is required for ${timeData.description || 'prayer time'}`);
            setIsLoading(false);
            return;
          }

          if (timeData.isNew) {
            // Create new prayer time
            await prayerService.createPrayerTime(groupId, {
              dayOfWeek: selectedDay,
              prayerType: prayerType,
              time: timeData.time12h.trim(),
              description: timeData.description.trim() || 'Service'
            });
          } else {
            // Update existing prayer time
            await prayerService.updatePrayerTime(groupId, timeData.id, {
              time: timeData.time12h.trim(),
              description: timeData.description.trim() || 'Service'
            });
          }
        }
      }

      // Notify parent component to refresh data
      await onPrayerTimesUpdate();
      
      // Close modal
      onClose();
    } catch (error) {
      console.error('Error saving prayer times:', error);
      setError(error.response?.data?.message || 'Failed to save prayer times');
    } finally {
      setIsLoading(false);
    }
  };

  // Format prayer type for display
  const formatPrayerType = (type) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  if (!isOpen) return null;

  return (
    <div className="prayer-edit-modal-overlay" onClick={onClose}>
      <div className="prayer-edit-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="prayer-edit-header">
          <h2>
            Edit {formatPrayerType(prayerType)} - {selectedDay}
          </h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="prayer-edit-error">
            {error}
          </div>
        )}

        {/* Prayer Times List */}
        <div className="prayer-times-editor">
          {editingTimes.length === 0 ? (
            <div className="no-prayer-times">
              <p>No prayer times scheduled for this service.</p>
            </div>
          ) : (
            editingTimes.map((time, index) => (
              <div key={time.id} className="prayer-time-editor">
                {time.isEditing ? (
                  // Edit Mode
                  <div className="edit-form">
                    <div className="form-group">
                      <label>
                        <FontAwesomeIcon icon={faTag} /> Description
                      </label>
                      <input
                        type="text"
                        value={time.description}
                        onChange={(e) => handleDescriptionChange(index, e.target.value)}
                        placeholder="e.g., Early Minyan"
                        disabled={isLoading}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        <FontAwesomeIcon icon={faClock} /> Time
                      </label>
                      <TimePicker
                        value={time.time24h || ''}
                        onChange={(newTime) => handleTimeChange(index, newTime)}
                        disabled={isLoading}
                      />
                    </div>
                    <div className="edit-actions">
                      <button 
                        className="save-button"
                        onClick={() => handleSave(index)}
                        disabled={isLoading || !time.time12h.trim()}
                      >
                        {isLoading ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div className="prayer-time-view">
                    <div className="time-info">
                      <span className="time">{time.time12h || time.time}</span>
                      <span className="description">{time.description}</span>
                    </div>
                    <div className="time-actions">
                      <button 
                        className="edit-action-button"
                        onClick={() => handleEdit(index)}
                        disabled={isLoading}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button 
                        className="delete-action-button"
                        onClick={() => handleDelete(index)}
                        disabled={isLoading}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}

          {/* Add New Button */}
          <button 
            className="add-new-button"
            onClick={handleAddNew}
            disabled={isLoading || editingTimes.some(t => t.isEditing)}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Prayer Time
          </button>
        </div>

        {/* Footer */}
        <div className="prayer-edit-footer">
          <button 
            className="done-button" 
            onClick={handleDoneWithSave}
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 
              editingTimes.some(item => item.isEditing) ? 'Save & Done' : 'Done'
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrayerEditModal;